import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import LocalPrintshopIcon from '@material-ui/icons/LocalPrintshop';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import AddressZoneDialog from './AddressZoneDialog';
import {
  getAddressZones,
  getAddressCoordinateId,
  deleteAddressZone,
} from '../../services/addressService';
import { ZONE_GRID_COLUMNS, DETAIL_FIELDS } from '../../constants/addressZonesTabConstants';
import { geocodeAddress, buildZoneDetailPrintHtml } from '../../utility/addressZoneHelper';
import FormSpinner from 'components/FormSpinner';

const AddressZonesTab = ({ ptsAddressID, fullAddressText }) => {
  const dispatch = useDispatch();
  const canEditAddress = usePermissions(PERMISSION_RESOURCES.ADDRESS).Edit;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);
  const printIframeRef = useRef(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadZones = useCallback(async () => {
    if (!ptsAddressID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: async () => {
        const ptsCoordinateID = await getAddressCoordinateId(ptsAddressID);
        const coords = ptsCoordinateID ? null : await geocodeAddress(fullAddressText);
        return getAddressZones({ ptsAddressID, ptsCoordinateID, coords });
      },
      onSuccess: (data) => setRows(data || []),
    });
  }, [dispatch, ptsAddressID, fullAddressText]);

  const existingZoneCodes = useMemo(
    () => rows.map((row) => ({ ZoneCode: row.ZoneCode, AgencyID: row.AgencyID })),
    [rows]
  );

  const handleAdd = useCallback(() => setFormOpen(true), []);
  const handleFormClose = useCallback(() => setFormOpen(false), []);
  const handleSaved = useCallback(() => {
    handleFormClose();
    loadZones();
  }, [handleFormClose, loadZones]);

  const handleViewDetails = useCallback((row) => setViewTarget(row), []);
  const handleCloseDetails = useCallback(() => setViewTarget(null), []);

  const handlePrint = useCallback(() => {
    if (!viewTarget) return;

    const html = buildZoneDetailPrintHtml(viewTarget, DETAIL_FIELDS);
    const win = printIframeRef.current.contentWindow;

    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  }, [viewTarget]);

  const handleDelete = useCallback(
    (row) => {
      if (!row.ptsZoneInfoID) {
        dispatch(notify('This zone is geofence-matched and cannot be removed here.', 'info'));
        return;
      }

      requestConfirm(
        `Are you sure you want to delete this "${row.ZoneCode}" zone?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteAddressZone(row.ptsZoneInfoID),
            onSuccess: () => {
              dispatch(notify('Zone removed successfully', 'success'));
              loadZones();
            },
          });
        },
        'Remove Zone'
      );
    },
    [dispatch, requestConfirm, loadZones]
  );

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  return (
    <Box className="person-names-tab__grid-wrap address-zones-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Zone"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditAddress}
        />
      </Box>

      <CommonDataGrid
        rows={rows}
        columns={ZONE_GRID_COLUMNS}
        getRowId={(row) => row.ZoneCode}
        toolbar={getDataGridToolbarProps({ printTitle: 'Address Zones' })}
        actions={{
          delete: { visible: true, onClick: handleDelete, disabled: !canEditAddress },
          custom: [
            {
              icon: <InfoIcon />,
              tooltip: 'View Details',
              onClick: handleViewDetails,
            },
          ],
        }}
      />

      {formOpen && (
        <AddressZoneDialog
          open={formOpen}
          ptsAddressID={ptsAddressID}
          existingZoneCodes={existingZoneCodes}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {viewTarget && (
        <RMSCommonDialog
          open={Boolean(viewTarget)}
          title={`${viewTarget.ZoneCode} - ${viewTarget.Description || ''}`}
          onClose={handleCloseDetails}
          maxWidth="xs"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}
          customButtons={
            <RMSButton
              label="Print"
              variant="contained"
              color="primary"
              prefixIcon={<LocalPrintshopIcon />}
              onClick={handlePrint}
            />
          }>
          <Grid container spacing={1}>
            {DETAIL_FIELDS.map(({ field, label }) => (
              <Grid item xs={6} key={field}>
                <Typography variant="caption" color="textSecondary" component="div">
                  {label}
                </Typography>
                <Typography variant="body2">{viewTarget[field] || '—'}</Typography>
              </Grid>
            ))}
          </Grid>

          <iframe
            ref={printIframeRef}
            title="address-zone-print-frame"
            className="address-zone-print-iframe"
          />
        </RMSCommonDialog>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default AddressZonesTab;
